/**
 * @fileoverview Product data model with JSDoc types
 * Products are sub-entities of Brands
 *
 * Part of Phase 1: Data Boundary Refactoring
 * @version 1.0.0
 */

/**
 * @typedef {Object} Product
 * @property {string} id - UUID
 * @property {string} brandId - Parent brand UUID
 * @property {string} name - Product name
 * @property {string} description - Product description
 * @property {string|null} imageUrl - Product image URL
 * @property {number} sortOrder - Display order within brand
 * @property {string} createdAt - ISO timestamp
 * @property {string|null} updatedAt - ISO timestamp
 */

/**
 * Creates a normalized Product object from raw API response
 * @param {Object} raw - Raw API response (snake_case)
 * @returns {Product|null}
 */
export function normalizeProduct(raw) {
  if (!raw) return null;

  return {
    id: raw.id,
    brandId: raw.brand_id || raw.brandId || null,
    name: raw.name || '',
    description: raw.description || '',
    imageUrl: raw.image_url || raw.imageUrl || null,
    sortOrder: raw.sort_order ?? raw.sortOrder ?? 0,
    createdAt: raw.created_at || raw.createdAt || new Date().toISOString(),
    updatedAt: raw.updated_at || raw.updatedAt || null
  };
}

/**
 * Normalize array of products
 * @param {Object[]} rawProducts
 * @returns {Product[]}
 */
export function normalizeProducts(rawProducts) {
  if (!Array.isArray(rawProducts)) return [];
  return rawProducts.map(normalizeProduct).filter(Boolean);
}

/**
 * Convert normalized Product back to database format
 * @param {Product} product
 * @returns {Object}
 */
export function productToDb(product) {
  if (!product) return null;

  return {
    id: product.id,
    brand_id: product.brandId,
    name: product.name,
    description: product.description,
    image_url: product.imageUrl,
    sort_order: product.sortOrder
  };
}
